import { useRef, useState } from 'react';
import { Sheet } from '../ui/controls';
import { Icon } from '../ui/icons';
import { prepareImage } from '../ui/fields';
import { attempt, backend, toast, useApp } from '../data/store';
import { MasjidColoursEditor } from '../ui/MasjidColours';
import { coloursFromImage } from '../app/palette';
import { MAX_MASJID_COLOURS, type Masjid } from '@noorview/core';

// Asked once (per masjid, on this device) before the Studio opens: the logo and the masjid's colours.
const SKIP = 'noorview.brand.skipped';
/** Whether to ask for the logo and colours before the Studio opens. */
export const brandPromptDue = (m: Masjid) => (!m.logo || !(m.colors ?? []).length) && !logoSkipped(m.id);
export const logoSkipped = (masjidId: string) => { try { return (JSON.parse(localStorage.getItem(SKIP) ?? '[]') as string[]).includes(masjidId); } catch { return false; } };
const skip = (masjidId: string) => { try { const a = JSON.parse(localStorage.getItem(SKIP) ?? '[]') as string[]; localStorage.setItem(SKIP, JSON.stringify([...new Set([...a, masjidId])])); } catch { /* ignore */ } };

/**
 * Upload or change the masjid's logo. Shown once before the Studio opens (with the masjid's colours too: found in the
 * logo, or added by hex code or the picker), and from Settings or the Logo widget.
 */
export function LogoSheet({ onClose, first }: { onClose: () => void; first?: boolean }) {
  const masjid = useApp((s) => s.ws?.masjid);
  const isAdmin = useApp((s) => s.session?.role !== 'editor');
  const [url, setUrl] = useState<string | null>(masjid?.logo ?? null);
  const [busy, setBusy] = useState(false);
  const [over, setOver] = useState(false);
  const [colors, setColors] = useState<string[]>(masjid?.colors ?? []);
  const input = useRef<HTMLInputElement>(null);
  if (!masjid) return null;
  const coloursChanged = first && JSON.stringify(colors) !== JSON.stringify(masjid.colors ?? []);

  const take = async (f: File | undefined) => {
    if (!f) return;
    setBusy(true);
    try {
      setUrl(await backend.uploadMedia(await prepareImage(f, 800), f.name));
      // First time, with no colours yet: the logo's colours, ready to keep or change.
      if (first && !colors.length) { const found = await coloursFromImage(f, MAX_MASJID_COLOURS); if (found.length) setColors(found); }
    }
    catch (x) { toast(x instanceof Error ? x.message : 'Upload failed.', { bad: true }); }
    finally { setBusy(false); }
  };
  const save = async () => {
    const logoChanged = url !== (masjid.logo ?? null);
    const msg = first ? 'Saved. Your logo is in the widget library as Masjid Logo, and your colours are first in every colour choice.' : url ? 'Logo saved. Find it in the widget library as Masjid Logo.' : 'Logo removed';
    if (await attempt(() => backend.updateMasjid({ ...(logoChanged ? { logo: url } : {}), ...(coloursChanged ? { colors } : {}) }), msg)) { if (first) skip(masjid.id); onClose(); }
  };
  const later = () => { if (first) skip(masjid.id); onClose(); };

  return (
    <Sheet open onClose={later} title={first ? 'Your masjid’s logo and colours' : 'Masjid logo'} closeLabel={first ? 'Skip for now' : 'Close'}
      subtitle={first ? 'Your logo goes in the widget library as Masjid Logo. Your colours come first in every colour choice in the Studio.' : 'It appears in the widget library as Masjid Logo, ready to drag onto any screen. A PNG with a see-through background looks best.'}
      footer={<>
        <button type="button" className="btn plain" onClick={later}>{first ? 'Skip for now' : 'Cancel'}</button>
        <button type="button" className="btn primary" disabled={busy || !isAdmin || (url === (masjid.logo ?? null) && !coloursChanged)} onClick={save}>{busy ? 'Uploading…' : first ? 'Save' : 'Save logo'}</button>
      </>}>
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/gif" hidden onChange={(e) => { void take(e.target.files?.[0]); e.target.value = ''; }} />
      <button type="button" className={`drop logo-drop${over ? ' over' : ''}`} disabled={busy || !isAdmin} onClick={() => input.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); void take(e.dataTransfer.files[0]); }}>
        {url ? <img src={url} alt={`${masjid.name} logo`} /> : <><Icon name="upload" size={32} /><b>Choose your logo</b><span className="hint">or drop the image here</span></>}
      </button>
      {!isAdmin && <div className="banner info">Only an admin can change the masjid’s logo.</div>}
      {url && isAdmin && <div className="row"><button type="button" className="btn sm plain" onClick={() => input.current?.click()}><Icon name="refresh" size={16} />Choose another</button>
        <span className="spacer" /><button type="button" className="btn sm plain danger-text" onClick={() => setUrl(null)}><Icon name="trash" size={16} />Remove</button></div>}
      {first && isAdmin && (
        <div className="field"><span>Masjid colours</span>
          <MasjidColoursEditor value={colors} onChange={setColors} logo={url} disabled={busy} />
        </div>
      )}
    </Sheet>
  );
}
